import { useEffect, useCallback } from 'react';

interface UseCrispReturn {
  openCrisp: () => void;
  hideCrisp: () => void;
  isCrispAvailable: boolean;
}

/**
 * Custom hook to manage Crisp chat integration
 * @param email - User's email address for Crisp identification
 * @param autoInitialize - Whether to automatically initialize Crisp on mount (default: true)
 * @returns Object with methods to control Crisp chat
 */
function useCrisp(email?: string, autoInitialize: boolean = true): UseCrispReturn {
  const isCrispAvailable = typeof window !== 'undefined' && typeof window.$crisp !== 'undefined';

  // Initialize Crisp when component mounts
  useEffect(() => {
    if (!autoInitialize || !isCrispAvailable) {
      return;
    }

    if (email) {
      window.$crisp.push(['set', 'user:email', [email]]);
    }

    // No cleanup needed for Crisp
  }, [email, autoInitialize, isCrispAvailable]);

  // Function to open Crisp chat
  const openCrisp = useCallback(() => {
    if (window.$crisp) {
      window.$crisp.push(['do', 'chat:show']);
      window.$crisp.push(['do', 'chat:open']);
    }
  }, []);

  // Function to hide Crisp chat (closes and hides the widget)
  const hideCrisp = useCallback(() => {
    if (window.$crisp) {
      window.$crisp.push(['do', 'chat:close']);
      window.$crisp.push(['do', 'chat:hide']);
    }
  }, []);

  return {
    openCrisp,
    hideCrisp,
    isCrispAvailable
  };
}

export default useCrisp;
